import classNames from 'clsx';
import { memoizeWith } from 'ramda';
import React, { type ReactElement, memo, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { type ListChildComponentProps, areEqual } from 'react-window';
import { getInlineEditState } from 'reactApp/modules/modifying/modifying.selectors';
import { AdvBanner } from 'reactApp/ui/Mobile/Datalist/AdvBanner/AdvBanner';

import {
    DATA_LIST_BREADCRUMBS_ID,
    DATA_LIST_EXTRABLOCKS_ID,
    DATA_LIST_GALLERY_TAB_MENU_ID,
    DATA_LIST_LAST_FILES_WIDGET_ID,
    DATA_LIST_PUBLIC_UPLOAD_HEADER_ID,
    DATA_LIST_STORIES_WIDGET_ID,
} from '../Datalist/DataList';
import { COMPENSATE_SCROLL_PADDING } from './VirtualList.constants';
import { VIRTUAL_LIST_LOADER_ID } from './VirtualList.new';
import styles from './VirtualList.new.css';

interface MemProps {
    width: number;
    height: number;
    margin: number;
    resultMargin: number;
    isTutoriaIntegration: boolean;
}

const getStyles = memoizeWith(
    (props: MemProps) => `${props.width}${props.height}${props.margin}${props.resultMargin}`,
    (props: MemProps) => {
        const itemStyle = {
            width: `${props.width}px`,
            // На мой взгляд, resultMargin вообще не нужен, поскольку сам margin уже подсчитан с учетом ширины экрана и отдельного элемента
            marginRight: `${props.isTutoriaIntegration ? props.margin : props.resultMargin}px`,
            height: `${props.height}px`,
        };
        const lastItemStyle = {
            width: `${props.width}px`,
            marginRight: 0,
            height: `${props.height}px`,
        };

        return { itemStyle, lastItemStyle };
    }
);

export const VirtualListGridRow = memo(function VirtualListGridRow(props: ListChildComponentProps): ReactElement | null {
    const { index, style, data } = props;
    const { list, width, margin, height, itemActiveIndex, indexes, dividerSize, columnCount, isTutoriaIntegration } = data;

    const { isInlineEditInProgress } = useSelector(getInlineEditState);

    let skipBreadcrumbsAndStoryWidget = 0;

    // Иначе выделение элемента ошибается на это число элементов в singleRowItem
    for (let i = 0; i < list.length && i < 2; i++) {
        if (
            list[i] === DATA_LIST_STORIES_WIDGET_ID ||
            list[i] === DATA_LIST_GALLERY_TAB_MENU_ID ||
            list[i] === DATA_LIST_BREADCRUMBS_ID ||
            list[i] === DATA_LIST_LAST_FILES_WIDGET_ID ||
            list[i] === DATA_LIST_PUBLIC_UPLOAD_HEADER_ID
        ) {
            skipBreadcrumbsAndStoryWidget++;
        }
    }

    const startIndex = indexes[index].start;
    const lastIndex = Math.min(indexes[index].end, list.length);
    const handleRef = index === itemActiveIndex ? data.handleRef : null;
    const isDivider = list[startIndex].divider;

    const singleRowItem =
        startIndex === lastIndex &&
        [
            DATA_LIST_EXTRABLOCKS_ID,
            VIRTUAL_LIST_LOADER_ID,
            DATA_LIST_BREADCRUMBS_ID,
            DATA_LIST_STORIES_WIDGET_ID,
            DATA_LIST_LAST_FILES_WIDGET_ID,
            DATA_LIST_GALLERY_TAB_MENU_ID,
            DATA_LIST_PUBLIC_UPLOAD_HEADER_ID,
        ].includes(list[startIndex]);

    const accumulatedMargin = (columnCount - 1) * margin;
    const resultMargin = (accumulatedMargin - COMPENSATE_SCROLL_PADDING * 2) / (columnCount - 1);

    const { itemStyle, lastItemStyle } = getStyles({ width, height, margin, resultMargin, isTutoriaIntegration });

    const content = useMemo(() => {
        if (singleRowItem) {
            return null;
        }

        const output: ReactElement[] = [];

        for (let i = startIndex; i <= lastIndex; i++) {
            const skipRenderItem = list[i]?.divider;

            if (skipRenderItem) {
                continue;
            }

            const key = String(typeof list[i] === 'string' ? list[i] : list[i].id);

            output.push(
                <div key={key + i} style={i === lastIndex ? lastItemStyle : itemStyle}>
                    <div className={styles.colItem}>{data.renderItem(list[i], { index: i - skipBreadcrumbsAndStoryWidget })}</div>
                </div>
            );
        }

        return output;
    }, [startIndex, lastIndex, data.renderItem, itemStyle, singleRowItem]);

    if (singleRowItem) {
        return (
            <div
                key={index}
                className={classNames({
                    [styles.singleRow]: !isInlineEditInProgress,
                })}
                style={style}
            >
                <div
                    className={classNames(styles.compensateScroll, {
                        [styles.singleRow]: !isInlineEditInProgress,
                    })}
                >
                    {data.renderItem(list[startIndex], { index: startIndex })}
                </div>
            </div>
        );
    }

    if (isDivider) {
        return (
            <div
                key={index}
                className={classNames({
                    [styles.dividerContainer]: true,
                    [styles.dividerGrid]: true,
                    [styles.dividerAdv]: list[startIndex].showAdv,
                })}
                style={style}
                data-qa-divider={list[startIndex].text}
            >
                <div
                    className={classNames(styles.compensateScroll, {
                        [styles.divider]: true,
                        [styles[`divider_${dividerSize}`]]: !!dividerSize,
                    })}
                >
                    {list[startIndex].text}
                    {list[startIndex].showAdv && <AdvBanner />}
                </div>
            </div>
        );
    }

    return (
        <div style={style} className={styles.gridRow} ref={handleRef}>
            {content}
        </div>
    );
}, areEqual);
